import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PageHeader from '../../PageHeader/PageHeader';
import { HitApi } from '../../Api/ApiHit';
import { searchBill } from '../../constant/Constant';
import { Users, UserPlus, IndianRupee, CalendarCheck, TrendingUp, Wallet, Eye, X, Calendar, Clock, AlertTriangle, CheckCircle, RefreshCw, Percent, Award, Scissors, Star, ShieldAlert, Gift } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, LineChart, Line, Legend } from 'recharts';
import toast from 'react-hot-toast';
import CustomModal from '../../components/CustomModal/CustumModal';
import AppTable from '../../components/AppTable/AppTable';
import { setPagination } from '../../redux/Actions/TableAction';
import ViewButton from '../../components/AppButton/ViewButton';

function ClientAnalytics() {
    const dispatch = useDispatch();
    const TableDataReducer = useSelector((state) => state.TableReducer);

    const [loading, setLoading] = useState(true);
    const [allBills, setAllBills] = useState([]);
    const [allClients, setAllClients] = useState([]);
    const [selectedClient, setSelectedClient] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Date filter states
    const [selectedFilter, setSelectedFilter] = useState('allTime');
    const [showCustomRange, setShowCustomRange] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const filterOptions = [
        { label: 'All Time', value: 'allTime' },
        { label: 'Today', value: 'today' },
        { label: 'Yesterday', value: 'yesterday' },
        { label: 'This Week', value: 'thisWeek' },
        { label: 'This Month', value: 'thisMonth' },
        { label: 'Last 7 Days', value: 'last7Days' },
        { label: 'Last 30 Days', value: 'last30Days' },
        { label: 'Last 3 Months', value: 'last3Months' },
        { label: 'Last 6 Months', value: 'last6Months' },
        { label: 'This Year', value: 'thisYear' },
        { label: 'Custom Range', value: 'customRange' },
    ];

    const [analyticsData, setAnalyticsData] = useState({
        totalClients: 0,
        newClientsThisMonth: 0,
        totalRevenue: 0,
        totalVisits: 0,
        totalUnpaidAmount: 0,
        avgRevenuePerClient: 0,
        genderDistribution: [],
        ageGroupDistribution: [],
        visitTrends: []
    });

    useEffect(() => {
        if (selectedFilter !== 'customRange') {
            fetchBillsAndAnalyze();
        }
    }, [selectedFilter]);

    const handleFilterChange = (value) => {
        setSelectedFilter(value);
        if (value === 'customRange') {
            setShowCustomRange(true);
        } else {
            setShowCustomRange(false);
            setStartDate('');
            setEndDate('');
        }
    };

    const handleApplyCustomRange = () => {
        if (startDate && endDate) {
            fetchBillsAndAnalyze();
        }
    };

    const getDateRangeTimestamps = () => {
        const now = new Date();
        let start, end;

        switch (selectedFilter) {
            case 'today':
                start = new Date(now).setHours(0, 0, 0, 0);
                end = new Date(now).setHours(23, 59, 59, 999);
                break;

            case 'yesterday':
                const yesterday = new Date(now);
                yesterday.setDate(now.getDate() - 1);
                start = yesterday.setHours(0, 0, 0, 0);
                end = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1).setHours(23, 59, 59, 999);
                break;

            case 'thisWeek':
                const weekStart = new Date(now);
                const day = now.getDay();
                const diff = now.getDate() - day + (day === 0 ? -6 : 1);
                weekStart.setDate(diff);
                start = weekStart.setHours(0, 0, 0, 0);
                end = new Date(now).setHours(23, 59, 59, 999);
                break;

            case 'thisMonth':
                start = new Date(now.getFullYear(), now.getMonth(), 1).setHours(0, 0, 0, 0);
                end = new Date(now).setHours(23, 59, 59, 999);
                break;

            case 'last7Days':
                const last7 = new Date(now);
                last7.setDate(now.getDate() - 7);
                start = last7.setHours(0, 0, 0, 0);
                end = new Date(now).setHours(23, 59, 59, 999);
                break;

            case 'last30Days':
                const last30 = new Date(now);
                last30.setDate(now.getDate() - 30);
                start = last30.setHours(0, 0, 0, 0);
                end = new Date(now).setHours(23, 59, 59, 999);
                break;

            case 'last3Months':
                const last3Months = new Date(now);
                last3Months.setMonth(now.getMonth() - 3);
                start = last3Months.setHours(0, 0, 0, 0);
                end = new Date(now).setHours(23, 59, 59, 999);
                break;

            case 'last6Months':
                const last6Months = new Date(now);
                last6Months.setMonth(now.getMonth() - 6);
                start = last6Months.setHours(0, 0, 0, 0);
                end = new Date(now).setHours(23, 59, 59, 999);
                break;

            case 'thisYear':
                start = new Date(now.getFullYear(), 0, 1).setHours(0, 0, 0, 0);
                end = new Date(now).setHours(23, 59, 59, 999);
                break;

            case 'customRange':
                if (startDate && endDate) {
                    start = new Date(startDate).setHours(0, 0, 0, 0);
                    end = new Date(endDate).setHours(23, 59, 59, 999);
                } else {
                    start = null;
                    end = null;
                }
                break;

            case 'allTime':
            default:
                start = null;
                end = null;
        }

        return { start, end };
    };

    const getDateRangeText = () => {
        const now = new Date();

        switch (selectedFilter) {
            case 'today':
                return now.toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });

            case 'yesterday':
                const yesterday = new Date(now);
                yesterday.setDate(now.getDate() - 1);
                return yesterday.toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });

            case 'thisWeek':
                const weekStart = new Date(now);
                const day = now.getDay();
                const diff = now.getDate() - day + (day === 0 ? -6 : 1);
                weekStart.setDate(diff);
                return `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

            case 'thisMonth':
                const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
                return `${monthStart.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} - ${now.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`;

            case 'last7Days':
                const last7 = new Date(now);
                last7.setDate(now.getDate() - 7);
                return `${last7.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

            case 'last30Days':
                const last30 = new Date(now);
                last30.setDate(now.getDate() - 30);
                return `${last30.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

            case 'last3Months':
                const last3Months = new Date(now);
                last3Months.setMonth(now.getMonth() - 3);
                return `${last3Months.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

            case 'last6Months':
                const last6Months = new Date(now);
                last6Months.setMonth(now.getMonth() - 6);
                return `${last6Months.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

            case 'thisYear':
                const yearStart = new Date(now.getFullYear(), 0, 1);
                return `${yearStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

            case 'customRange':
                if (startDate && endDate) {
                    const start = new Date(startDate);
                    const end = new Date(endDate);
                    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
                }
                return 'Select custom date range';

            case 'allTime':
                return 'All Time';

            default:
                return '';
        }
    };

    const fetchBillsAndAnalyze = async () => {
        setLoading(true);
        try {
            const { start, end } = getDateRangeTimestamps();

            const searchObj = {};

            // Apply date filter if not "allTime"
            if (start && end) {
                searchObj.createdAt = {
                    $gte: start,
                    $lte: end
                };
            }

            const json = {
                page: 1,
                limit: 100000,
                search: searchObj
            };

            const res = await HitApi(json, searchBill);

            if (res?.statusCode === 200 && res?.data?.docs) {
                const bills = res.data.docs;
                setAllBills(bills);
                analyzeData(bills);
            } else {
                toast.error('Failed to fetch bills data');
            }
        } catch (error) {
            console.error('Error fetching bills:', error);
            toast.error('Error fetching data');
        } finally {
            setLoading(false);
        }
    };

    const analyzeData = (bills) => {
        const clientMap = new Map();
        const currentDate = new Date();
        const currentMonth = currentDate.getMonth();
        const currentYear = currentDate.getFullYear();

        const monthlyVisits = {};
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        for (let i = 5; i >= 0; i--) {
            const date = new Date(currentYear, currentMonth - i, 1);
            const key = `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
            monthlyVisits[key] = { month: monthNames[date.getMonth()], visits: 0, revenue: 0 };
        }

        let totalRevenue = 0;
        let totalUnpaid = 0;

        bills.forEach((bill) => {
            const client = bill.client;
            if (!client) return;

            const clientId = client._id || client.phoneNumber || 'unknown';
            const billDate = new Date(bill.timestamp || bill.createdAt);
            const billAmount = bill.calculations?.totals?.grandTotal || 0;
            const unpaidAmount = bill.payment?.paymentStatus !== 'Paid' ? billAmount - (bill.payment?.totalPaid || 0) : 0;

            totalRevenue += billAmount;
            totalUnpaid += unpaidAmount > 0 ? unpaidAmount : 0;

            if (clientMap.has(clientId)) {
                const existingClient = clientMap.get(clientId);
                existingClient.totalVisits += 1;
                existingClient.totalSpent += billAmount;
                existingClient.unpaidAmount += unpaidAmount > 0 ? unpaidAmount : 0;
                existingClient.visitDates.push(billDate);
                if (billDate > existingClient.lastVisit) {
                    existingClient.lastVisit = billDate;
                }
                if (billDate < existingClient.firstVisit) {
                    existingClient.firstVisit = billDate;
                }
            } else {
                clientMap.set(clientId, {
                    id: clientId,
                    name: client.name || 'Unknown',
                    phoneNumber: client.phoneNumber || 'N/A',
                    gender: client.gender || 'Unknown',
                    ageGroup: client.ageGroup || 'Unknown',
                    totalVisits: 1,
                    totalSpent: billAmount,
                    unpaidAmount: unpaidAmount > 0 ? unpaidAmount : 0,
                    lastVisit: billDate,
                    firstVisit: billDate,
                    visitDates: [billDate]
                });
            }

            const monthKey = `${monthNames[billDate.getMonth()]} ${billDate.getFullYear()}`;
            if (monthlyVisits[monthKey]) {
                monthlyVisits[monthKey].visits += 1;
                monthlyVisits[monthKey].revenue += billAmount;
            }
        });

        const clients = Array.from(clientMap.values());
        const totalClients = clients.length;

        const newClientsThisMonth = clients.filter(client => {
            const firstVisit = new Date(client.firstVisit);
            return firstVisit.getMonth() === currentMonth && firstVisit.getFullYear() === currentYear;
        }).length;

        const genderCount = {};
        clients.forEach(client => {
            const gender = client.gender || 'Unknown';
            genderCount[gender] = (genderCount[gender] || 0) + 1;
        });
        const genderDistribution = Object.entries(genderCount).map(([name, value]) => ({
            name,
            value,
            percentage: ((value / totalClients) * 100).toFixed(1)
        }));

        const ageGroupCount = {};
        clients.forEach(client => {
            const ageGroup = client.ageGroup || 'Unknown';
            ageGroupCount[ageGroup] = (ageGroupCount[ageGroup] || 0) + 1;
        });
        const ageGroupDistribution = Object.entries(ageGroupCount).map(([name, value]) => ({
            name,
            value,
            percentage: ((value / totalClients) * 100).toFixed(1)
        }));

        const visitTrends = Object.values(monthlyVisits);

        // Sort all clients by totalSpent (descending)
        const sortedClients = clients.sort((a, b) => b.totalSpent - a.totalSpent);

        const totalVisits = bills.length;

        // Store all clients separately for pagination
        setAllClients(sortedClients);

        // Set pagination in Redux
        dispatch(setPagination({
            total: sortedClients.length,
            page: 1,
            limit: TableDataReducer.pagination?.limit || 10
        }));

        setAnalyticsData({
            totalClients,
            newClientsThisMonth,
            totalRevenue,
            totalVisits,
            totalUnpaidAmount: totalUnpaid,
            avgRevenuePerClient: totalClients > 0 ? totalRevenue / totalClients : 0,
            genderDistribution,
            ageGroupDistribution,
            visitTrends
        });
    };

    // Get current page data based on Redux pagination
    const currentPage = TableDataReducer.pagination?.page || 1;
    const limit = TableDataReducer.pagination?.limit || 10;
    const startIndex = (currentPage - 1) * limit;
    const endIndex = startIndex + limit;
    const currentClients = allClients.slice(startIndex, endIndex);

    const formatCurrency = (amount) => {
        return `₹${Number(amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
    };

    const calculateDaysSince = (date) => {
        const now = new Date();
        const visitDate = new Date(date);
        const diffTime = Math.abs(now - visitDate);
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    };

    const calculateAverageVisitGap = (visitDates) => {
        if (visitDates.length < 2) return null;
        const sortedDates = [...visitDates].sort((a, b) => new Date(a) - new Date(b));
        let totalGap = 0;
        for (let i = 1; i < sortedDates.length; i++) {
            const gap = (new Date(sortedDates[i]) - new Date(sortedDates[i - 1])) / (1000 * 60 * 60 * 24);
            totalGap += gap;
        }
        return Math.round(totalGap / (sortedDates.length - 1));
    };

    const calculateExpectedReturn = (lastVisit, avgGap) => {
        if (!avgGap) return null;
        const expectedDate = new Date(lastVisit);
        expectedDate.setDate(expectedDate.getDate() + avgGap);
        return expectedDate;
    };

    const getReturnStatus = (expectedDate, daysSinceLastVisit, avgGap) => {
        if (!expectedDate || !avgGap) return { status: 'unknown', color: 'gray', message: 'Not enough data' };

        const today = new Date();
        const daysOverdue = Math.ceil((today - expectedDate) / (1000 * 60 * 60 * 24));

        if (daysOverdue > 7) {
            return { status: 'overdue', color: 'red', message: `${daysOverdue} days overdue` };
        } else if (daysOverdue > 0) {
            return { status: 'due', color: 'orange', message: `Due ${daysOverdue} days ago` };
        } else if (daysOverdue > -7) {
            return { status: 'soon', color: 'yellow', message: `Expected in ${Math.abs(daysOverdue)} days` };
        } else {
            return { status: 'ok', color: 'green', message: `Expected in ${Math.abs(daysOverdue)} days` };
        }
    };

    const handleViewClient = (client) => {
        // Get all bills for this client
        const clientBills = allBills.filter(bill => {
            const billClientId = bill.client?._id || bill.client?.phoneNumber;
            return billClientId === client.id;
        }).sort((a, b) => new Date(b.timestamp || b.createdAt) - new Date(a.timestamp || a.createdAt));

        const avgGap = calculateAverageVisitGap(client.visitDates);
        const expectedReturn = calculateExpectedReturn(client.lastVisit, avgGap);
        const daysSinceLastVisit = calculateDaysSince(client.lastVisit);
        const returnStatus = getReturnStatus(expectedReturn, daysSinceLastVisit, avgGap);

        // Analyze services used by this client
        const serviceAnalysis = analyzeClientServices(clientBills);

        // Calculate discount recommendation
        const discountRecommendation = calculateDiscountRecommendation(client, daysSinceLastVisit, returnStatus, serviceAnalysis);

        setSelectedClient({
            ...client,
            bills: clientBills,
            avgVisitGap: avgGap,
            expectedReturn,
            daysSinceLastVisit,
            returnStatus,
            serviceAnalysis,
            discountRecommendation
        });
        setIsModalOpen(true);
    };

    // Analyze services used by client
    const analyzeClientServices = (bills) => {
        const serviceMap = new Map();
        let totalServiceSpent = 0;

        console.log('Analyzing services from bills:', bills.length);

        bills.forEach(bill => {
            const services = bill.services || [];
            console.log('Bill services:', services);

            services.forEach(service => {
                const serviceName = service.name || service.serviceName || 'Unknown Service';

                // Extract price from nested pricing object (correct structure based on BillMaster.js)
                const basePrice = service.pricing?.basePrice || service.pricing?.totalPrice || 0;
                const finalPrice = service.pricing?.finalPrice || service.pricing?.totalPrice || 0;
                const servicePrice = finalPrice; // Use final price for total spent
                const serviceDiscount = basePrice - finalPrice; // Calculate discount from basePrice - finalPrice
                const quantity = service.quantity || 1;

                console.log('Service:', serviceName, 'Final Price:', finalPrice, 'Base Price:', basePrice, 'Discount:', serviceDiscount, 'Quantity:', quantity);

                if (serviceMap.has(serviceName)) {
                    const existing = serviceMap.get(serviceName);
                    existing.count += quantity;
                    existing.totalSpent += Number(servicePrice);
                    existing.totalDiscount += Number(serviceDiscount);
                } else {
                    serviceMap.set(serviceName, {
                        name: serviceName,
                        count: quantity,
                        totalSpent: Number(servicePrice),
                        totalDiscount: Number(serviceDiscount),
                        basePrice: basePrice
                    });
                }
                totalServiceSpent += Number(servicePrice);
            });
        });

        // Convert to array and sort by count (most used first)
        const servicesArray = Array.from(serviceMap.values())
            .sort((a, b) => b.count - a.count)
            .map(service => ({
                ...service,
                avgPrice: service.count > 0 ? service.totalSpent / service.count : 0,
                usagePercentage: totalServiceSpent > 0 ? ((service.totalSpent / totalServiceSpent) * 100).toFixed(1) : 0
            }));

        console.log('Service analysis result:', servicesArray);

        return {
            services: servicesArray,
            totalServiceSpent,
            uniqueServicesCount: servicesArray.length,
            mostUsedService: servicesArray[0] || null
        };
    };

    // Calculate discount recommendation based on client loyalty and churn risk
    const calculateDiscountRecommendation = (client, daysSinceLastVisit, returnStatus, serviceAnalysis) => {
        let recommendedDiscount = 0;
        let churnRisk = 'low';
        let loyaltyTier = 'new';
        let reason = '';

        // Determine loyalty tier based on visits and spending
        const totalVisits = client.totalVisits || 0;
        const totalSpent = client.totalSpent || 0;

        if (totalVisits >= 20 || totalSpent >= 50000) {
            loyaltyTier = 'platinum';
        } else if (totalVisits >= 10 || totalSpent >= 25000) {
            loyaltyTier = 'gold';
        } else if (totalVisits >= 5 || totalSpent >= 10000) {
            loyaltyTier = 'silver';
        } else if (totalVisits >= 2) {
            loyaltyTier = 'bronze';
        } else {
            loyaltyTier = 'new';
        }

        // Determine churn risk based on return status
        if (returnStatus?.status === 'overdue') {
            churnRisk = 'high';
        } else if (returnStatus?.status === 'due') {
            churnRisk = 'medium';
        } else if (daysSinceLastVisit > 30) {
            churnRisk = 'medium';
        } else {
            churnRisk = 'low';
        }

        // Calculate recommended discount
        // Base discount by loyalty tier
        const tierDiscounts = {
            'platinum': 15,
            'gold': 10,
            'silver': 7,
            'bronze': 5,
            'new': 0
        };
        recommendedDiscount = tierDiscounts[loyaltyTier];

        // Add bonus for high churn risk
        if (churnRisk === 'high') {
            recommendedDiscount += 10;
            reason = 'High churn risk - Client overdue for visit';
        } else if (churnRisk === 'medium') {
            recommendedDiscount += 5;
            reason = 'Medium churn risk - Consider retention offer';
        } else {
            reason = `${loyaltyTier.charAt(0).toUpperCase() + loyaltyTier.slice(1)} tier loyalty reward`;
        }

        // Cap maximum discount at 25%
        recommendedDiscount = Math.min(recommendedDiscount, 25);

        // Determine which service to discount (most used service)
        const serviceToDiscount = serviceAnalysis?.mostUsedService?.name || 'Any service';

        return {
            recommendedDiscount,
            loyaltyTier,
            churnRisk,
            reason,
            serviceToDiscount,
            estimatedSavings: serviceAnalysis?.mostUsedService
                ? Math.round((serviceAnalysis.mostUsedService.avgPrice * recommendedDiscount) / 100)
                : 0
        };
    };

    const COLORS = ['#6366F1', '#EC4899', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];
    const GENDER_COLORS = {
        'Male': '#3B82F6',
        'Female': '#EC4899',
        'Other': '#10B981',
        'Unknown': '#9CA3AF'
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white p-3 rounded-xl shadow-xl border border-gray-100">
                    <p className="text-sm font-semibold text-gray-900 mb-1">{label}</p>
                    {payload.map((entry, index) => (
                        <p key={index} className="text-sm" style={{ color: entry.color }}>
                            {entry.name}: {entry.name === 'Revenue' ? formatCurrency(entry.value) : entry.value}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    // Table headers for AppTable
    const tableHeaders = [
        {
            title: "S.No",
            key: "serialNumber",
            width: "70px",
            align: "center",
            render: (_, row, index) => {
                const globalIndex = startIndex + index;
                return (
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mx-auto ${globalIndex === 0 ? 'bg-gradient-to-br from-yellow-400 to-amber-500 text-white shadow-lg' :
                        globalIndex === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-white shadow-lg' :
                            globalIndex === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-500 text-white shadow-lg' :
                                'bg-gray-100 text-gray-600'
                        }`}>
                        {globalIndex + 1}
                    </div>
                );
            }
        },
        {
            title: "Client",
            key: "name",
            render: (value, row) => (
                <div>
                    <p className="font-semibold text-gray-900">{row.name}</p>
                    <p className="text-xs text-gray-500">{row.phoneNumber}</p>
                </div>
            )
        },
        {
            title: "Gender",
            key: "gender",
            render: (value) => (
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${value === 'Male' ? 'bg-blue-100 text-blue-700' :
                    value === 'Female' ? 'bg-pink-100 text-pink-700' :
                        'bg-gray-100 text-gray-600'
                    }`}>
                    {value}
                </span>
            )
        },
        {
            title: "Visits",
            key: "totalVisits",
            align: "center",
            render: (value) => (
                <span className="inline-flex items-center justify-center w-10 h-10 bg-violet-100 text-violet-700 rounded-full text-sm font-bold">
                    {value}
                </span>
            )
        },
        {
            title: "Total Spent",
            key: "totalSpent",
            align: "right",
            render: (value) => (
                <p className="font-bold text-emerald-600">{formatCurrency(value)}</p>
            )
        },
        {
            title: "Unpaid",
            key: "unpaidAmount",
            align: "right",
            render: (value) => (
                <span className={`font-semibold ${value > 0 ? 'text-rose-600' : 'text-gray-400'}`}>
                    {value > 0 ? formatCurrency(value) : '-'}
                </span>
            )
        },
        {
            title: "Last Visit",
            key: "lastVisit",
            render: (value) => {
                const daysSince = calculateDaysSince(value);
                return (
                    <div>
                        <p className="text-sm text-gray-900">
                            {new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </p>
                        <p className={`text-xs font-medium ${daysSince > 30 ? 'text-rose-500' : daysSince > 14 ? 'text-amber-500' : 'text-emerald-500'}`}>
                            {daysSince} days ago
                        </p>
                    </div>
                );
            }
        },
        {
            title: "Action",
            key: "action",
            align: "start",
            render: (_, row) => (
                <ViewButton onClick={(e) => {
                    e.stopPropagation();
                    handleViewClient(row);
                }} itemName="client" />
            )
        }
    ];

    if (loading) {
        return (
            <div className="p-5">
                <PageHeader
                    title="Client Analytics"
                    description="Analyzing client data from bills..."
                />
                <div className="flex justify-center items-center h-64">
                    <div className="flex flex-col items-center gap-4">
                        <div className="animate-spin rounded-full h-16 w-16 border-4 border-teal-200 border-t-teal-600"></div>
                        <p className="text-gray-500 animate-pulse">Loading analytics...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='p-5'>
            {/* Header Section with Date Filter */}
            <div className="bg-white rounded-xl p-4 mb-4 border border-gray-100">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="bg-gradient-to-r from-teal-600 to-cyan-600 p-2 rounded-lg">
                            <Users className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-gray-800">Client Analytics</h1>
                            <div className="flex items-center gap-1.5 mt-0.5">
                                <Calendar className="w-3.5 h-3.5 text-teal-600" />
                                <span className="text-xs text-teal-600 font-medium">{getDateRangeText()}</span>
                            </div>
                        </div>
                    </div>

                    {/* Filter Dropdown and Refresh Button */}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => {
                                fetchBillsAndAnalyze();
                                toast.success('Analytics refreshed');
                            }}
                            disabled={loading}
                            className="p-2 border border-gray-200 rounded-lg bg-white text-gray-600 hover:bg-teal-50 hover:border-teal-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Refresh analytics"
                        >
                            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                        <select
                            value={selectedFilter}
                            onChange={(e) => handleFilterChange(e.target.value)}
                            className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white text-gray-700 text-sm font-medium cursor-pointer hover:border-teal-300 transition-all"
                        >
                            {filterOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Custom Date Range Picker */}
                {showCustomRange && (
                    <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 mt-3">
                        <div className="flex items-center gap-3 flex-wrap">
                            <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-teal-600" />
                                <span className="text-sm font-medium text-gray-700">Custom Range:</span>
                            </div>

                            <div className="flex items-center gap-2">
                                <label className="text-xs text-gray-600">From:</label>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="px-2 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white text-sm"
                                />
                            </div>

                            <div className="flex items-center gap-2">
                                <label className="text-xs text-gray-600">To:</label>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    className="px-2 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white text-sm"
                                />
                            </div>

                            <button
                                onClick={handleApplyCustomRange}
                                disabled={!startDate || !endDate}
                                className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${startDate && endDate
                                    ? 'bg-teal-600 text-white hover:bg-teal-700'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    }`}
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Overview Cards - Gradient Style */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
                {/* Total Clients */}
                <div className="relative overflow-hidden bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-5 text-white shadow-lg shadow-blue-200">
                    <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-white/10 rounded-full"></div>
                    <div className="absolute bottom-0 left-0 -mb-4 -ml-4 w-16 h-16 bg-white/10 rounded-full"></div>
                    <div className="relative">
                        <div className="flex items-center justify-between mb-3">
                            <Users className="w-8 h-8 opacity-80" />
                            <span className="text-xs bg-white/20 px-2 py-1 rounded-full">Total</span>
                        </div>
                        <p className="text-3xl font-bold">{analyticsData.totalClients}</p>
                        <p className="text-sm opacity-80 mt-1">Total Clients</p>
                    </div>
                </div>

                {/* New This Month */}
                <div className="relative overflow-hidden bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-5 text-white shadow-lg shadow-emerald-200">
                    <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-white/10 rounded-full"></div>
                    <div className="absolute bottom-0 left-0 -mb-4 -ml-4 w-16 h-16 bg-white/10 rounded-full"></div>
                    <div className="relative">
                        <div className="flex items-center justify-between mb-3">
                            <UserPlus className="w-8 h-8 opacity-80" />
                            <span className="text-xs bg-white/20 px-2 py-1 rounded-full">This Month</span>
                        </div>
                        <p className="text-3xl font-bold">{analyticsData.newClientsThisMonth}</p>
                        <p className="text-sm opacity-80 mt-1">New Clients</p>
                    </div>
                </div>

                {/* Total Revenue */}
                <div className="relative overflow-hidden bg-gradient-to-br from-violet-500 to-violet-600 rounded-2xl p-5 text-white shadow-lg shadow-violet-200">
                    <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-white/10 rounded-full"></div>
                    <div className="absolute bottom-0 left-0 -mb-4 -ml-4 w-16 h-16 bg-white/10 rounded-full"></div>
                    <div className="relative">
                        <div className="flex items-center justify-between mb-3">
                            <IndianRupee className="w-8 h-8 opacity-80" />
                            <span className="text-xs bg-white/20 px-2 py-1 rounded-full">Revenue</span>
                        </div>
                        <p className="text-2xl font-bold">{formatCurrency(analyticsData.totalRevenue)}</p>
                        <p className="text-sm opacity-80 mt-1">Total Revenue</p>
                    </div>
                </div>

                {/* Total Visits */}
                <div className="relative overflow-hidden bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl p-5 text-white shadow-lg shadow-amber-200">
                    <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-white/10 rounded-full"></div>
                    <div className="absolute bottom-0 left-0 -mb-4 -ml-4 w-16 h-16 bg-white/10 rounded-full"></div>
                    <div className="relative">
                        <div className="flex items-center justify-between mb-3">
                            <CalendarCheck className="w-8 h-8 opacity-80" />
                            <span className="text-xs bg-white/20 px-2 py-1 rounded-full">Bills</span>
                        </div>
                        <p className="text-3xl font-bold">{analyticsData.totalVisits}</p>
                        <p className="text-sm opacity-80 mt-1">Total Visits</p>
                    </div>
                </div>

                {/* Avg Revenue */}
                <div className="relative overflow-hidden bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-2xl p-5 text-white shadow-lg shadow-cyan-200">
                    <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-white/10 rounded-full"></div>
                    <div className="absolute bottom-0 left-0 -mb-4 -ml-4 w-16 h-16 bg-white/10 rounded-full"></div>
                    <div className="relative">
                        <div className="flex items-center justify-between mb-3">
                            <TrendingUp className="w-8 h-8 opacity-80" />
                            <span className="text-xs bg-white/20 px-2 py-1 rounded-full">Average</span>
                        </div>
                        <p className="text-2xl font-bold">{formatCurrency(Math.round(analyticsData.avgRevenuePerClient))}</p>
                        <p className="text-sm opacity-80 mt-1">Avg/Client</p>
                    </div>
                </div>

                {/* Total Unpaid */}
                <div className="relative overflow-hidden bg-gradient-to-br from-rose-500 to-rose-600 rounded-2xl p-5 text-white shadow-lg shadow-rose-200">
                    <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-white/10 rounded-full"></div>
                    <div className="absolute bottom-0 left-0 -mb-4 -ml-4 w-16 h-16 bg-white/10 rounded-full"></div>
                    <div className="relative">
                        <div className="flex items-center justify-between mb-3">
                            <Wallet className="w-8 h-8 opacity-80" />
                            <span className="text-xs bg-white/20 px-2 py-1 rounded-full">Pending</span>
                        </div>
                        <p className="text-2xl font-bold">{formatCurrency(analyticsData.totalUnpaidAmount)}</p>
                        <p className="text-sm opacity-80 mt-1">Total Unpaid</p>
                    </div>
                </div>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Gender Distribution */}
                <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-bold text-gray-900">Gender Distribution</h3>
                        <span className="text-xs bg-blue-50 text-blue-600 px-3 py-1 rounded-full font-medium">
                            {analyticsData.totalClients} Clients
                        </span>
                    </div>
                    {analyticsData.genderDistribution.length > 0 ? (
                        <div className="flex flex-col md:flex-row items-center">
                            <ResponsiveContainer width="100%" height={220}>
                                <PieChart>
                                    <Pie
                                        data={analyticsData.genderDistribution}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={50}
                                        outerRadius={90}
                                        paddingAngle={3}
                                        dataKey="value"
                                        stroke="none"
                                    >
                                        {analyticsData.genderDistribution.map((entry, index) => (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill={GENDER_COLORS[entry.name] || COLORS[index % COLORS.length]}
                                            />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="flex flex-col gap-3 mt-4 md:mt-0 md:ml-6">
                                {analyticsData.genderDistribution.map((entry, index) => (
                                    <div key={index} className="flex items-center gap-3 bg-gray-50 px-4 py-2 rounded-xl">
                                        <div
                                            className="w-4 h-4 rounded-full shadow-sm"
                                            style={{ backgroundColor: GENDER_COLORS[entry.name] || COLORS[index % COLORS.length] }}
                                        />
                                        <div>
                                            <p className="text-sm font-semibold text-gray-900">{entry.name}</p>
                                            <p className="text-xs text-gray-500">{entry.value} ({entry.percentage}%)</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center justify-center h-64 text-gray-400">No data available</div>
                    )}
                </div>

                {/* Age Group Distribution */}
                <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-bold text-gray-900">Age Group Distribution</h3>
                        <span className="text-xs bg-teal-50 text-teal-600 px-3 py-1 rounded-full font-medium">
                            Demographics
                        </span>
                    </div>
                    {analyticsData.ageGroupDistribution.length > 0 ? (
                        <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={analyticsData.ageGroupDistribution} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorBar" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#14B8A6" stopOpacity={1} />
                                        <stop offset="100%" stopColor="#0D9488" stopOpacity={0.8} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                                <XAxis dataKey="name" tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} />
                                <Tooltip content={<CustomTooltip />} />
                                <Bar dataKey="value" fill="url(#colorBar)" radius={[8, 8, 0, 0]} maxBarSize={45} />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="flex items-center justify-center h-64 text-gray-400">No data available</div>
                    )}
                </div>
            </div>

            {/* Visit Trends */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-shadow mb-8">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-bold text-gray-900">Visit & Revenue Trends</h3>
                    <span className="text-xs bg-violet-50 text-violet-600 px-3 py-1 rounded-full font-medium">
                        Last 6 Months
                    </span>
                </div>
                {analyticsData.visitTrends.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                        <LineChart data={analyticsData.visitTrends} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                            <defs>
                                <linearGradient id="colorVisits" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.1} />
                                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="month" tick={{ fill: '#6b7280', fontSize: 12 }} axisLine={false} tickLine={false} />
                            <YAxis yAxisId="left" tick={{ fill: '#6b7280', fontSize: 12 }} axisLine={false} tickLine={false} />
                            <YAxis yAxisId="right" orientation="right" tick={{ fill: '#6b7280', fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`} />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                            <Line yAxisId="left" type="monotone" dataKey="visits" stroke="#3B82F6" strokeWidth={3} dot={{ fill: '#3B82F6', r: 5, strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 8 }} name="Visits" />
                            <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={3} dot={{ fill: '#10B981', r: 5, strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 8 }} name="Revenue" />
                        </LineChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="flex items-center justify-center h-64 text-gray-400">No data available</div>
                )}
            </div>

            {/* All Clients Table with AppTable */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
                    <div>
                        <h3 className="text-lg font-bold text-gray-900">All Clients</h3>
                        <p className="text-sm text-gray-500">Total {allClients.length} clients based on billing data</p>
                    </div>
                </div>

                <AppTable
                    TH={tableHeaders}
                    TD={currentClients}
                    isLoading={loading}
                    emptyMessage="No client data available"
                />
            </div>

            {/* Client Forecasting Modal */}
            <CustomModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} size="5xl">
                {selectedClient && (
                    <div className="p-4 max-h-[90vh] overflow-y-auto">
                        {/* Modal Header */}
                        <div className="flex justify-between items-start mb-4 pb-3 border-b">
                            <div className="flex items-center gap-3">
                                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg ${selectedClient.discountRecommendation?.loyaltyTier === 'platinum' ? 'bg-gradient-to-br from-slate-600 to-slate-800' :
                                    selectedClient.discountRecommendation?.loyaltyTier === 'gold' ? 'bg-gradient-to-br from-yellow-500 to-amber-600' :
                                        selectedClient.discountRecommendation?.loyaltyTier === 'silver' ? 'bg-gradient-to-br from-gray-400 to-gray-500' :
                                            selectedClient.discountRecommendation?.loyaltyTier === 'bronze' ? 'bg-gradient-to-br from-orange-600 to-orange-700' :
                                                'bg-gradient-to-br from-blue-500 to-blue-600'
                                    }`}>
                                    {selectedClient.name?.charAt(0)?.toUpperCase()}
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h2 className="text-xl font-bold text-gray-900">{selectedClient.name}</h2>
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase ${selectedClient.discountRecommendation?.loyaltyTier === 'platinum' ? 'bg-slate-200 text-slate-700' :
                                            selectedClient.discountRecommendation?.loyaltyTier === 'gold' ? 'bg-yellow-100 text-yellow-700' :
                                                selectedClient.discountRecommendation?.loyaltyTier === 'silver' ? 'bg-gray-200 text-gray-600' :
                                                    selectedClient.discountRecommendation?.loyaltyTier === 'bronze' ? 'bg-orange-100 text-orange-700' :
                                                        'bg-blue-100 text-blue-600'
                                            }`}>
                                            {selectedClient.discountRecommendation?.loyaltyTier || 'New'}
                                        </span>
                                    </div>
                                    <p className="text-sm text-gray-500">{selectedClient.phoneNumber}</p>
                                </div>
                            </div>
                            <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                                <X className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>

                        {/* Discount Recommendation Section */}
                        <div className="bg-gradient-to-r from-teal-500 to-emerald-500 rounded-xl p-4 mb-4 text-white">
                            <div className="flex items-start justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="bg-white/20 p-2 rounded-lg">
                                        <Gift className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-lg">Discount Recommendation</h3>
                                        <p className="text-sm text-white/80">{selectedClient.discountRecommendation?.reason}</p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="text-4xl font-bold">{selectedClient.discountRecommendation?.recommendedDiscount || 0}%</p>
                                    <p className="text-xs text-white/80">Recommended Discount</p>
                                </div>
                            </div>
                            <div className="mt-3 pt-3 border-t border-white/20 grid grid-cols-3 gap-4">
                                <div>
                                    <p className="text-xs text-white/70">Apply On</p>
                                    <p className="font-semibold text-sm">{selectedClient.discountRecommendation?.serviceToDiscount}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-white/70">Estimated Savings</p>
                                    <p className="font-semibold text-sm">{formatCurrency(selectedClient.discountRecommendation?.estimatedSavings || 0)}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-white/70">Churn Risk</p>
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${selectedClient.discountRecommendation?.churnRisk === 'high' ? 'bg-red-100 text-red-700' :
                                        selectedClient.discountRecommendation?.churnRisk === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                            'bg-green-100 text-green-700'
                                        }`}>
                                        <ShieldAlert className="w-3 h-3" />
                                        {selectedClient.discountRecommendation?.churnRisk?.toUpperCase()}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Visit Pattern Cards */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                            <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                                <div className="flex items-center gap-2 mb-1">
                                    <Clock className="w-4 h-4 text-blue-600" />
                                    <span className="text-xs font-medium text-blue-700">Last Visit</span>
                                </div>
                                <p className={`text-2xl font-bold ${selectedClient.daysSinceLastVisit > 30 ? 'text-rose-600' : selectedClient.daysSinceLastVisit > 14 ? 'text-amber-600' : 'text-blue-600'}`}>
                                    {selectedClient.daysSinceLastVisit}
                                </p>
                                <p className="text-xs text-gray-500">days ago</p>
                            </div>
                            <div className="bg-violet-50 rounded-lg p-3 border border-violet-100">
                                <div className="flex items-center gap-2 mb-1">
                                    <Calendar className="w-4 h-4 text-violet-600" />
                                    <span className="text-xs font-medium text-violet-700">Avg Gap</span>
                                </div>
                                <p className="text-2xl font-bold text-violet-600">{selectedClient.avgVisitGap || 'N/A'}</p>
                                <p className="text-xs text-gray-500">days</p>
                            </div>
                            <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
                                <div className="flex items-center gap-2 mb-1">
                                    <TrendingUp className="w-4 h-4 text-amber-600" />
                                    <span className="text-xs font-medium text-amber-700">Expected Return</span>
                                </div>
                                <p className="text-sm font-bold text-amber-600">
                                    {selectedClient.expectedReturn
                                        ? new Date(selectedClient.expectedReturn).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
                                        : 'N/A'
                                    }
                                </p>
                                <p className="text-xs text-gray-500">next visit</p>
                            </div>
                            <div className={`rounded-lg p-3 border ${selectedClient.returnStatus?.color === 'red' ? 'bg-rose-50 border-rose-100' :
                                selectedClient.returnStatus?.color === 'orange' ? 'bg-orange-50 border-orange-100' :
                                    selectedClient.returnStatus?.color === 'green' ? 'bg-emerald-50 border-emerald-100' :
                                        'bg-gray-50 border-gray-100'
                                }`}>
                                <div className="flex items-center gap-2 mb-1">
                                    {selectedClient.returnStatus?.color === 'red' || selectedClient.returnStatus?.color === 'orange' ? (
                                        <AlertTriangle className={`w-4 h-4 ${selectedClient.returnStatus?.color === 'red' ? 'text-rose-600' : 'text-orange-600'}`} />
                                    ) : (
                                        <CheckCircle className="w-4 h-4 text-emerald-600" />
                                    )}
                                    <span className="text-xs font-medium text-gray-700">Status</span>
                                </div>
                                <p className={`text-sm font-bold ${selectedClient.returnStatus?.color === 'red' ? 'text-rose-600' :
                                    selectedClient.returnStatus?.color === 'orange' ? 'text-orange-600' :
                                        'text-emerald-600'
                                    }`}>
                                    {selectedClient.returnStatus?.message || 'Unknown'}
                                </p>
                            </div>
                        </div>

                        {/* Two Column Layout */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                            {/* Service Analysis */}
                            <div className="bg-white rounded-xl border border-gray-200 p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="font-bold text-gray-900 flex items-center gap-2">
                                        <Scissors className="w-4 h-4 text-teal-600" />
                                        Service Usage
                                    </h3>
                                    <span className="text-xs bg-teal-50 text-teal-600 px-2 py-1 rounded-full">
                                        {selectedClient.serviceAnalysis?.uniqueServicesCount || 0} Services
                                    </span>
                                </div>
                                {selectedClient.serviceAnalysis?.services?.length > 0 ? (
                                    <div className="space-y-2 max-h-48 overflow-y-auto">
                                        {selectedClient.serviceAnalysis.services.slice(0, 5).map((service, index) => (
                                            <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-2">
                                                <div className="flex items-center gap-2">
                                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${index === 0 ? 'bg-yellow-100 text-yellow-700' :
                                                        index === 1 ? 'bg-gray-200 text-gray-600' :
                                                            index === 2 ? 'bg-orange-100 text-orange-600' :
                                                                'bg-blue-50 text-blue-600'
                                                        }`}>
                                                        {index + 1}
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-900">{service.name}</p>
                                                        <p className="text-xs text-gray-500">{service.count}x used</p>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-sm font-bold text-gray-900">{formatCurrency(service.totalSpent)}</p>
                                                    <p className="text-xs text-teal-600">{service.usagePercentage}%</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-6 text-gray-400">
                                        <Scissors className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                        <p className="text-sm">No service data available</p>
                                    </div>
                                )}
                            </div>

                            {/* Summary Stats */}
                            <div className="bg-white rounded-xl border border-gray-200 p-4">
                                <h3 className="font-bold text-gray-900 flex items-center gap-2 mb-3">
                                    <Star className="w-4 h-4 text-amber-500" />
                                    Client Summary
                                </h3>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="bg-blue-50 rounded-lg p-3 text-center">
                                        <p className="text-2xl font-bold text-blue-600">{selectedClient.totalVisits}</p>
                                        <p className="text-xs text-gray-500">Total Visits</p>
                                    </div>
                                    <div className="bg-emerald-50 rounded-lg p-3 text-center">
                                        <p className="text-xl font-bold text-emerald-600">{formatCurrency(selectedClient.totalSpent)}</p>
                                        <p className="text-xs text-gray-500">Total Spent</p>
                                    </div>
                                    <div className="bg-rose-50 rounded-lg p-3 text-center">
                                        <p className="text-xl font-bold text-rose-600">{selectedClient.unpaidAmount > 0 ? formatCurrency(selectedClient.unpaidAmount) : '₹0'}</p>
                                        <p className="text-xs text-gray-500">Unpaid</p>
                                    </div>
                                    <div className="bg-purple-50 rounded-lg p-3 text-center">
                                        <p className="text-xl font-bold text-purple-600">{formatCurrency(selectedClient.totalSpent / (selectedClient.totalVisits || 1))}</p>
                                        <p className="text-xs text-gray-500">Avg/Visit</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Visit History */}
                        <div className="bg-white rounded-xl border border-gray-200 p-4">
                            <h3 className="font-bold text-gray-900 mb-3">Visit History</h3>
                            <div className="max-h-40 overflow-y-auto space-y-2">
                                {selectedClient.bills?.slice(0, 10).map((bill, index) => (
                                    <div key={bill._id || index} className="flex items-center justify-between bg-gray-50 rounded-lg p-2 hover:bg-gray-100 transition-colors">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                                <span className="text-xs font-bold text-blue-600">{selectedClient.bills.length - index}</span>
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-gray-900">
                                                    {new Date(bill.timestamp || bill.createdAt).toLocaleDateString('en-IN', {
                                                        day: '2-digit', month: 'short', year: 'numeric'
                                                    })}
                                                </p>
                                                <p className="text-xs text-gray-500">Bill #{bill.billNumber || 'N/A'}</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-sm font-bold text-gray-900">{formatCurrency(bill.calculations?.totals?.grandTotal || 0)}</p>
                                            <span className={`text-xs px-2 py-0.5 rounded-full ${bill.payment?.paymentStatus === 'Paid' ? 'bg-emerald-100 text-emerald-700' :
                                                bill.payment?.paymentStatus === 'Partial' ? 'bg-amber-100 text-amber-700' :
                                                    'bg-rose-100 text-rose-700'
                                                }`}>
                                                {bill.payment?.paymentStatus || 'Pending'}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </CustomModal>
        </div>
    );
}

export default ClientAnalytics;
